import type { Emitter } from "@core/events/emitter";
import type {
  MaterialKeywords,
  MaterialShaderFamily,
  MaterialShaderVariantId,
  MaterialSurface,
  MaterialWorkflow,
} from "./materialShaderVariants";

/**
 * Web material inspector contract.
 *
 * Mirrors the editor-facing responsibilities split across Unity's
 * `IMaterialModel`, `IMaterialLibraryModel`, `LookViewModel`, and
 * `MaterialLibraryViewModel`: selected-entity material previews, active
 * material editing, and a browsable material-library surface.
 */
export interface MaterialPreview {
  readonly id: string;
  readonly displayName: string;
  readonly previewColor: string;
  readonly shader: string;
  readonly shaderFamily?: MaterialShaderFamily;
  readonly slotLabel?: string;
  readonly thumbnailUrl?: string;
}

export interface MaterialLibraryGroup {
  readonly id: string;
  readonly name: string;
  readonly materials: ReadonlyArray<MaterialPreview>;
}

export interface EditableMaterial {
  readonly id: string;
  /**
   * `MaterialData.Name`, verbatim — the value that goes back to the row.
   *
   * Split from {@link EditableMaterial.displayName} because that one is a
   * REWRITER, not a formatter: it rejects GUID-shaped and long unspaced names
   * (`isIdLikeMaterialName`) and substitutes the shader's readable name. That
   * is right for a label and destructive for a save — a whole-item PutItem
   * carrying the derived value renames a Unity-authored material to "Lit" on
   * the first edit of any unrelated field, and the desktop client sees the
   * rename. Unity stores `material.name` verbatim (`MaterialData.cs:405`) and
   * only ever changes it through `SetName`, which is what the Name row does
   * here.
   */
  readonly name: string;
  /** DERIVED, view-only. Never written back — see {@link EditableMaterial.name}. */
  readonly displayName: string;
  readonly thumbnailUrl?: string;
  readonly shader: string;
  readonly shaderVariant: MaterialShaderVariantId;
  readonly shaderFamily: MaterialShaderFamily;
  readonly isConfigurableShader: boolean;
  readonly workflow: MaterialWorkflow;
  readonly surface: MaterialSurface;
  readonly baseColor: string;
  readonly baseColorAlpha: number;
  readonly specColor: string;
  /**
   * `SpecColor.a`. Unity's picker opens with `isAlphaEnabled: true` for all
   * four material colours (`MaterialViewModel.cs:1051-1107`), so a non-unit
   * alpha is authorable and is stored — and before this field existed the web
   * read only the RGB and wrote `a: 1` back on every save, destroying it.
   */
  readonly specColorAlpha: number;
  readonly emissionColor: string;
  readonly emissionColorAlpha: number;
  readonly fresnelColor: string;
  /** `FresnelColor.a`. Same reason as {@link EditableMaterial.specColorAlpha}. */
  readonly fresnelColorAlpha: number;
  readonly fresnelPower: number;
  readonly metallic: number;
  readonly smoothness: number;
  readonly smoothnessLow: number;
  readonly normalScale: number;
  readonly occlusionStrength: number;
  readonly clearCoat: number;
  readonly clearCoatGlossiness: number;
  readonly clearCoatGlossinessScale: number;
  readonly alphaCutoff: number;
  readonly useVertexColor: boolean;
  readonly doubleFaced: boolean;
  readonly textureIds: Readonly<Record<string, string>>;
  readonly textureFilterModes: Readonly<Record<string, string>>;
  readonly tiling: Readonly<{ x: number; y: number }>;
  readonly offset: Readonly<{ x: number; y: number }>;
  readonly keywords: MaterialKeywords;
}

/**
 * `displayName` is excluded because it is DERIVED — patching it would write a
 * label into `MaterialData.Name`. Rename through `name`.
 */
export type MaterialPatch = Partial<Omit<EditableMaterial, "id" | "displayName">>;

/**
 * The six texture slots the material editor authors, in the row order
 * `MaterialView.prefab` lays them out. Keys are shader property names, which is
 * exactly what `MaterialData.Textures` is keyed by
 * (`MaterialData.CopyTextureToMaterialData`, `MaterialData.cs:595`).
 */
export const MATERIAL_TEXTURE_SLOTS = [
  "_BaseMap",
  "_SpecGlossMap",
  "_MetallicGlossMap",
  "_OcclusionMap",
  "_BumpMap",
  "_EmissionMap",
] as const;

export type MaterialTextureSlot = (typeof MATERIAL_TEXTURE_SLOTS)[number];

/** What `TextureFilePicker` accepts (`Controls/TextureFilePicker.cs:60`). */
export const MATERIAL_TEXTURE_ACCEPT = ".jpg,.jpeg,.png,.webp";

/**
 * What an entity's glTF materials ACTUALLY look like on screen.
 *
 * A web-imported asset writes no material rows (deviation D1), so its parts
 * declare no `MaterialIDs` and the renderer leaves the glTF's own materials
 * alone. The inspector still has to show something, and until this seam it
 * showed a material synthesized from `colorFromString(assetId)` — a
 * pseudo-random pastel with no relationship to the model. That made the Look
 * swatch a lie, and it made the first real edit restyle the whole model to a
 * colour nobody chose.
 *
 * The renderer is the only thing that knows, so it answers; the model owns
 * what is done with the answer. Same direction as `IPlacementGeometryHost`.
 */
export interface EntitySourceMaterialSample {
  /** `#rrggbb`, from the glTF material's own base colour. */
  readonly baseColor: string;
  readonly baseColorAlpha: number;
  /** 0..100, the inspector's scale — not three's 0..1. */
  readonly metallic: number;
  /** 0..100. three stores roughness, which is `100 - smoothness`. */
  readonly smoothness: number;
  readonly doubleFaced: boolean;
  readonly surface: MaterialSurface;
  /**
   * True when the sampled material carries any map. Editing such a material
   * REPLACES the glTF's, and the Campfire material has no texture ids to put
   * back — see the note on `updateMaterial`.
   */
  readonly hasTextures: boolean;
}

export interface IEntitySourceMaterialHost {
  /** Null when the entity is not built yet, or has no sampleable mesh. */
  readEntitySourceMaterial(
    sceneId: string,
    entityId: string,
  ): EntitySourceMaterialSample | null;
}

export interface IMaterialModel {
  readonly onMaterialChanged: Emitter<{ materialId: string; entityId: string | null }>;
  readonly onActiveMaterialChanged: Emitter<EditableMaterial | null>;
  readonly onLibraryChanged: Emitter<void>;

  readonly activeMaterial: EditableMaterial | null;

  /** Registered by the viewport; `null` on unmount. See the host's doc. */
  attachSourceMaterialHost(host: IEntitySourceMaterialHost | null): void;

  getEntityMaterials(sceneId: string, snapshotId: string): ReadonlyArray<MaterialPreview>;
  /**
   * Every editable material across a selection SET, de-duplicated. Unity gets
   * this for free — `SelectedGameObjectModel` sets `m_hasEditableMaterials =
   * GetMaterials().Length > 0` on the `MultiExposeToEditor` wrapper (`:1158`),
   * which walks the members parented under it. The web has no wrapper, so the
   * union is taken here.
   */
  getSelectionSetMaterials(
    sceneId: string,
    memberSnapshotIds: ReadonlyArray<string>,
  ): ReadonlyArray<MaterialPreview>;
  getMaterialsForEntitySlots(
    entityId: string,
    materialIds: ReadonlyArray<string>,
  ): ReadonlyArray<EditableMaterial>;
  getSnapshotMaterials(sceneId: string, snapshotId: string): ReadonlyArray<EditableMaterial>;
  getEntityMaterial(sceneId: string, entityId: string): EditableMaterial | null;
  setActiveMaterial(materialId: string | null): void;
  getMaterial(materialId: string): EditableMaterial | null;
  /**
   * Apply an inspector edit, persist the row, and broadcast it.
   *
   * An entity that declares no `MaterialIDs` is shown a SYNTHESIZED material
   * (see `EntitySourceMaterialSample`). Editing one MATERIALIZES it: a real
   * row is minted, written, and the entity's root snapshot is re-pointed at
   * it, which is what makes the edit both visible and durable. Before that it
   * wrote nothing at all and returned silently — the edit lived in this
   * model's map until the page was reloaded.
   *
   * KNOWN COST of materializing, recorded rather than hidden: the minted
   * material is entity-WIDE, so a model whose glTF carries several distinct
   * materials collapses onto one, and embedded glTF maps are dropped (a
   * Campfire material addresses textures by S3 id, and a web import uploads
   * none — deviation D1). The real fix is writing material + texture rows at
   * import; until then `hasTextures` on the sample is what warns the caller.
   */
  updateMaterial(materialId: string, patch: MaterialPatch): Promise<void>;
  /**
   * Assign or clear one texture slot — the web's `TextureFilePicker`
   * (`Controls/TextureFilePicker.cs`) plus the upload leg Unity defers to save
   * time (`ProjectModel_Entities.UpdateMaterialAsync` -> `FromMaterial` mints a
   * GUID for an un-named texture -> `UploadTextureFilesAsync` PUTs it to
   * `textures/{id}`).
   *
   * `file` null CLEARS the slot. Unity deliberately leaves the S3 object behind
   * on a clear (`ProjectDataModel.UpdateMaterialAsync` passes
   * `deleteStorageData: false`, with the comment "we may need them to restore"),
   * and so does this — undo aside, a texture id can be referenced by another
   * material.
   *
   * Runs the same fork/materialize preamble as {@link IMaterialModel.updateMaterial},
   * so assigning a texture to a library-owned or synthesized material is safe.
   */
  setMaterialTexture(
    materialId: string,
    slot: string,
    file: Blob | null,
  ): Promise<void>;
  /**
   * A displayable URL for one texture id, or null when it cannot be resolved.
   *
   * Both id shapes go through here: a bundled material-library FILENAME
   * (`brick.png`, served from `public/materials/textures`) and a customer GUID
   * whose bytes live in S3 at `textures/{id}` — the same split
   * `CampfireMaterialBuilder.loadImage` makes, kept in one place so the
   * inspector's thumbnail and the rendered surface can never disagree about
   * what a slot holds.
   */
  resolveTextureUrl(textureId: string): Promise<string | null>;
  getLibraryGroups(): ReadonlyArray<MaterialLibraryGroup>;
  /**
   * True when the texture belongs to the shared material library. Port of
   * `IMaterialLibraryModel.ContainsTexture`, consumed by
   * `ProjectDataModel.IsSharedTexture` — library textures are referenced by
   * copied materials rather than duplicated into new S3 objects.
   */
  isSharedTexture(textureId: string): boolean;
  applyLibraryMaterial(targetMaterialId: string, libraryMaterialId: string): Promise<void>;
  applyLibraryMaterialToSnapshot(
    sceneId: string,
    snapshotId: string,
    libraryMaterialId: string,
  ): Promise<void>;
}

export const MATERIAL_MODEL = Symbol.for("Campfire.MaterialModel");
export const MATERIAL_DRAG_MIME = "application/x-campfire-material-id";
